import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Calendar, History, ChevronDown } from "lucide-react";
import { fetchList } from "../api";
import { PageHero, Loader } from "../components/Shared";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T00:00:00") : new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

export default function Championships() {
  const [champs, setChamps] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { fetchList("championships").then(setChamps).catch(() => setChamps([])); }, []);
  if (!champs) return <Loader />;

  return (
    <div data-testid="championships-page">
      <PageHero kicker="Who Holds The Gold?" title="Championships" image="https://images.unsplash.com/photo-1675364756276-3acb1e8bd5d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="px-6 md:px-12 lg:px-20 pb-24 space-y-8">
        {champs.map((c, i) => {
          const isOpen = openId === c.id;
          const history = c.history || [];
          return (
            <motion.div key={c.id} data-testid={`championship-${c.id}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 5) * 0.1 }}
              className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 group">
                <div className={`relative aspect-[16/10] overflow-hidden ${i % 2 ? "md:order-2" : ""}`}>
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: c.img_pos || "center" }} />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  {c.division && (<div className="flex items-center gap-2 text-brand mb-3"><Trophy size={18} /><span className="font-mono text-xs uppercase tracking-widest">{c.division} Division</span></div>)}
                  <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none tracking-tight">{c.title}</h2>
                  <div className="mt-6">
                    <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Current Champion</div>
                    <div className="font-display text-4xl text-brand uppercase mt-1">{c.champion}</div>
                  </div>
                  <div className="flex gap-8 mt-6">
                    {c.since && <div><div className="flex items-center gap-1 text-zinc-500 font-mono text-[10px] uppercase tracking-widest"><Calendar size={12} /> Since</div><div className="text-white mt-1">{fmtDate(c.since)}</div></div>}
                    {!!c.reign_days && <div><div className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Reign</div><div className="text-white mt-1">{c.reign_days} days</div></div>}
                  </div>
                  {history.length > 0 && (
                    <button
                      data-testid={`title-history-toggle-${c.id}`}
                      onClick={() => setOpenId(isOpen ? null : c.id)}
                      className="mt-8 w-fit inline-flex items-center gap-2 border-2 border-brand text-brand hover:bg-brand hover:text-black font-display text-lg uppercase tracking-wide px-5 py-2 transition-colors"
                    >
                      <History size={16} /> Title History
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </div>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    data-testid={`title-history-${c.id}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-zinc-800 overflow-hidden"
                  >
                    <div className="p-8 md:p-12">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="w-8 h-[2px] bg-brand" />
                        <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Reign History</span>
                      </div>
                      <ol className="relative border-l-2 border-zinc-800 ml-2">
                        {history.map((h, idx) => (
                          <li key={idx} className="ml-6 pb-8 last:pb-0">
                            <span className="absolute -left-[9px] w-4 h-4 bg-brand" />
                            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{fmtDate(h.date)}</div>
                            <div className="font-display text-2xl uppercase tracking-tight text-white mt-1">{h.champion}</div>
                            {h.note && <p className="text-zinc-400 mt-1 leading-relaxed">{h.note}</p>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
        {champs.length === 0 && <p className="text-zinc-500">No championships listed.</p>}
      </section>
    </div>
  );
}
